/**
 * SW2-2440. Shrink a photo in the browser before it is uploaded.
 *
 * Phones send 4032×3024 JPEGs of 2.5–5 MB, and on cellular the upload time
 * follows the bytes (4 finding photos took ~18 s). Sizes, by path:
 *
 * - Finding photos → 1200 short edge. The API re-saves every finding photo
 *   600 px tall (`rotateAndReplaceUpload`: 800×600, or 450×600 portrait), so
 *   this loses nothing that is stored, and leaves headroom if that changes.
 * - Asset and site photos → 2048 short edge (2731×2048). This IS a change:
 *   site photos were stored at full size, and so were asset photos unless the
 *   API's `ENABLE_IMAGE_COMPRESSION` flag is on. Chosen deliberately (SW2-2440):
 *   2731×2048 is still a 5.6 MP photo and uploads several times faster.
 *
 * The whole photo is kept: it is scaled, never cropped, and the aspect ratio
 * is preserved, so annotations still line up.
 *
 * GPS and the EXIF date are dropped by the canvas, so callers must read them
 * from the ORIGINAL file first. Every current caller already does (they send
 * them as separate form fields); the server never reads EXIF from the file.
 *
 * Only JPEGs are touched. Anything else, or any failure, returns the original
 * file unchanged, which is exactly what was uploaded before this change.
 */

/** Asset and site photos (see above: smaller than what was stored before). */
export const PHOTO_MAX_SHORT_EDGE = 2048;
/** Finding photos: twice the 600 px the API re-saves them at. */
export const FINDING_PHOTO_MAX_SHORT_EDGE = 1200;
export const PHOTO_JPEG_QUALITY = 0.85;

const RESIZABLE_TYPES = ['image/jpeg', 'image/jpg'];

/** Target size for a photo, or null when it is already small enough. */
export function getResizedDimensions(
  width: number,
  height: number,
  maxShortEdge: number = PHOTO_MAX_SHORT_EDGE,
): { width: number; height: number } | null {
  const shortEdge = Math.min(width, height);

  if (!width || !height || shortEdge <= maxShortEdge) return null;

  const scale = maxShortEdge / shortEdge;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

type Decoded = {
  source: ImageBitmap | HTMLImageElement;
  width: number;
  height: number;
  release: () => void;
};

function supportsImageOrientation(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('image-orientation', 'from-image')
  );
}

/**
 * Decode with EXIF orientation applied, so the canvas output is upright and the
 * dropped orientation tag no longer matters.
 */
async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      // TS 3.9's DOM types predate 'from-image' (they list only 'flipY' | 'none').
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
        // eslint-disable-next-line no-undef -- a DOM lib type; no-undef cannot see TS types
      } as unknown as ImageBitmapOptions);

      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      // Older Safari rejects the options argument; fall through to <img>.
    }
  }

  // An <img> drawn to a canvas is EXIF-oriented only where the browser applies
  // `image-orientation: from-image` (Chrome 81+, Safari 13.1+, verified on both
  // paths in Chromium and WebKit). Anywhere older, a sideways-tagged photo would
  // be re-encoded sideways with its tag dropped, so send the original instead.
  if (!supportsImageOrientation()) {
    throw new Error('image-orientation unsupported');
  }

  const url = URL.createObjectURL(file);
  const img = new Image();

  try {
    img.src = url;
    await img.decode();
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }

  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    release: () => URL.revokeObjectURL(url),
  };
}

function toJpeg(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
}

/**
 * Returns a smaller JPEG with the same name, or the original file when it is
 * not a JPEG, is already small enough, cannot be decoded, or would not shrink.
 */
export async function resizePhoto<T>(
  file: T,
  {
    maxShortEdge = PHOTO_MAX_SHORT_EDGE,
    quality = PHOTO_JPEG_QUALITY,
  }: { maxShortEdge?: number; quality?: number } = {},
): Promise<T | File> {
  // Offline-sync flows can carry a base64 string here instead of a File.
  if (!(file instanceof Blob) || !RESIZABLE_TYPES.includes(file.type)) {
    return file;
  }

  const blobIn: Blob = file;
  let decoded: Decoded | undefined;
  const canvas = document.createElement('canvas');

  try {
    decoded = await decode(blobIn);

    const target = getResizedDimensions(
      decoded.width,
      decoded.height,
      maxShortEdge,
    );

    if (!target) return file;

    canvas.width = target.width;
    canvas.height = target.height;

    const ctx = canvas.getContext('2d');

    if (!ctx) return file;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(decoded.source, 0, 0, target.width, target.height);

    const blob = await toJpeg(canvas, quality);

    if (!blob || blob.size >= blobIn.size) return file;

    const original = blobIn as Partial<File>;

    return new File([blob], original.name || 'photo.jpg', {
      type: 'image/jpeg',
      lastModified: original.lastModified ?? Date.now(),
    });
  } catch {
    return file;
  } finally {
    decoded?.release();
    // iOS caps total canvas memory per page; free this one straight away.
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** Resize one at a time: decoding several 12 MP photos at once can exhaust iPhone memory. */
export async function resizePhotos<T>(files: T[]): Promise<(T | File)[]> {
  const resized: (T | File)[] = [];

  for (const file of files) {
    resized.push(await resizePhoto(file));
  }

  return resized;
}
